import React, { useCallback, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { Empty, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import SortableOrderCard from "./SortableOrderCard";
import TripOrderCard, { ITripOrderCardData } from "../trip-order-card";
import styles from "./style.module.scss";

const { Text } = Typography;

export interface ITripOrderListProps {
  orders: ITripOrderCardData[];
  selectedOrderId?: number | null;
  isEditable?: boolean;
  isLoading?: boolean;
  onOrdersReorder?: (orders: ITripOrderCardData[]) => void;
  onOrderRemove?: (orderId: number) => void;
  onOrderSelect?: (orderId: number) => void;
}

const TripOrderList: React.FC<ITripOrderListProps> = ({
  orders,
  selectedOrderId = null,
  isEditable = true,
  isLoading = false,
  onOrdersReorder,
  onOrderRemove,
  onOrderSelect,
}) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = React.useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedOrders = useMemo(
    () => [...orders].sort((a, b) => a.sequence - b.sequence),
    [orders]
  );

  const orderIds = useMemo(
    () => sortedOrders.map((order) => order.id),
    [sortedOrders]
  );

  const activeOrder = useMemo(
    () => sortedOrders.find((order) => order.id === activeId),
    [sortedOrders, activeId]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = sortedOrders.findIndex((o) => o.id === active.id);
      const newIndex = sortedOrders.findIndex((o) => o.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedOrders = arrayMove(sortedOrders, oldIndex, newIndex).map(
          (order, index) => ({
            ...order,
            sequence: index + 1,
          })
        );
        onOrdersReorder?.(reorderedOrders);
      }
    },
    [sortedOrders, onOrdersReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description={
            <Text type="secondary">{t("no_orders_in_trip")}</Text>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.orderListContainer}>
      <div className={styles.listWrapper} role="list" aria-label={t("trip_orders")}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={orderIds}
            strategy={verticalListSortingStrategy}
          >
            {sortedOrders.map((order, index) => (
              <SortableOrderCard
                key={order.id}
                order={order}
                index={index}
                isSelected={selectedOrderId === order.id}
                isEditable={isEditable}
                onRemove={onOrderRemove}
                onSelect={onOrderSelect}
              />
            ))}
          </SortableContext>

          <DragOverlay>
            {activeOrder ? (
              <TripOrderCard
                order={activeOrder}
                index={sortedOrders.findIndex((o) => o.id === activeOrder.id)}
                isDragging
                isEditable={false}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {isEditable && (
        <div className={styles.footer}>
          <Text type="secondary" className={styles.hint}>
            {t("drag_to_reorder")}
          </Text>
        </div>
      )}
    </div>
  );
};

export default React.memo(TripOrderList);
